import { useEffect, useRef, useState } from 'react'

import { Box, Flex, Text, Tooltip } from '@chakra-ui/react'
import styled from '@emotion/styled'

import { MapPin } from '@/components/icons'
import { DrawerListCell } from '@/components/ui'
import { DeviceStatus, DeviceType } from '@/graphql/generated/schemas'

import { DevicePopoverContent } from '../../floor-plan-popover'
import { getDeviceIcon } from '../../utils/DeviceIcons'

interface IProps {
  deviceId: string
  placed: boolean
  selected: boolean
  deviceName?: string
  deviceType?: DeviceType
  deviceHealth: DeviceStatus
  onDeviceItemHoverIn?: (deviceId: string) => void
  onDeviceItemHoverOut?: () => void
}

const IconWrapperStyled = styled(Box)`
  .icon {
    width: 35px;
    margin-right: 5px;
    margin-left: auto;
  }
`

export const DeviceListItem = ({
  deviceId,
  deviceName,
  placed,
  selected,
  deviceType,
  deviceHealth,
  onDeviceItemHoverIn,
  onDeviceItemHoverOut,
}: IProps) => {
  const icon = getDeviceIcon(deviceType, deviceHealth)

  return (
    <DrawerListCell
      _hover={{
        boxShadow: placed ? 'md' : 'xl',
      }}
      bg='#fff'
      cursor={placed ? 'pointer' : 'grab'}
      onMouseEnter={() => onDeviceItemHoverIn?.(deviceId)}
      onMouseLeave={() => onDeviceItemHoverOut?.()}
      outline={selected ? '2px #2E6CDB' : 'inherit'}
    >
      <Tooltip
        background='white'
        label={
          <DevicePopoverContent
            deviceId={deviceId}
            deviceName={deviceName}
            deviceType={deviceType}
          />
        }
        openDelay={250}
        p={0}
        placement='left'
      >
        <Flex align='center' padding='10px' width='100%'>
          <Box pos='relative'>{icon}</Box>
          <Box isTruncated paddingLeft='10px'>
            <OverflownText>{deviceName}</OverflownText>
          </Box>
          {placed && (
            <Box flexGrow={1}>
              <IconWrapperStyled alignSelf='center'>
                <MapPin className='icon' strokeColor='#000' />
              </IconWrapperStyled>
            </Box>
          )}
        </Flex>
      </Tooltip>
    </DrawerListCell>
  )
}

const OverflownText = ({ children }: { children: React.ReactNode }) => {
  const ref = useRef(null)
  const [isOverflown, setIsOverflown] = useState(false)

  useEffect(() => {
    const element = ref.current
    setIsOverflown(element.scrollWidth > element.clientWidth)
  }, [])

  return (
    <Tooltip
      bgColor='#fff'
      boxShadow='md'
      color='#282424'
      fontSize='14px'
      fontWeight='semibold'
      isDisabled={!isOverflown}
      label={children}
      p='2'
      placement='top'
      rounded='5px'
    >
      <Text
        color='#282424'
        data-testid='floorPlanPage_devicePanelItem_name'
        fontSize='12px'
        fontWeight='semibold'
        isTruncated
        ref={ref}
      >
        {children}
      </Text>
    </Tooltip>
  )
}
